import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Port, RedundancyType } from 'icreate/common/models/port';
import { Subject, fromEvent, lastValueFrom, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { DuplicateDestinationDialogComponent } from '@components/duplicate-destination-dialog/duplicate-destination-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { CommunicationProtocol } from 'icreate/common/models/communication-protocol';
import { PortSettingsDialogComponent } from '@components/port-settings-dialog/port-settings-dialog.component';
import { FcuExternalCommunications } from 'icreate/common/models/fcu-unit';

@Component({
    selector: 'app-ports-table',
    templateUrl: './ports-table.component.html',
    styleUrl: './ports-table.component.css',
    standalone: false,
})
export class PortsTableComponent implements OnInit, OnDestroy {
    @Input() ports: Port[];
    @Input() communicationProtocols: CommunicationProtocol[];
    @Input() unitId: string;
    @Input() commsList: FcuExternalCommunications[];
    @Input() isCeuUnit: boolean = false;

    visiblePortOptions: Port;

    initialPorts: Port[];
    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', [])
    clickOut() {
        this.visiblePortOptions = null;
    }

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        this.initialPorts = _.cloneDeep(this.ports);
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.visiblePortOptions = null;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openOptions(port): void {
        this.visiblePortOptions = port;
    }

    async edit(port: Port, index) {
        const initialPortData = _.cloneDeep(port);

        const dialogRef = this.dialog.open(PortSettingsDialogComponent, {
            width: '532px',
            data: {
                port,
                communicationProtocols: this.communicationProtocols,
                unitId: this.unitId,
                commsList: this.commsList,
                isCeuUnit: this.isCeuUnit,
            },
        });

        const updatedSettings: Port = await lastValueFrom(dialogRef.afterClosed());

        if (!updatedSettings) {
            this.ports[index] = _.cloneDeep(initialPortData);
            return;
        }

        if (updatedSettings?.redundancy) {
            const target = this.findPort(updatedSettings.redundancyUnitId, updatedSettings.redundancyPortIndex);

            if (target) {
                const prevUnitId = target.redundancyUnitId;
                const prevPortIndex = target.redundancyPortIndex;

                if (prevUnitId && prevUnitId !== this.unitId) {
                    const prevPartner = this.findPort(prevUnitId, prevPortIndex);
                    if (prevPartner) this.clearRedundancy(prevPartner);
                }

                target.redundancy = true;
                target.redundancyType =
                    updatedSettings.redundancyType === RedundancyType.Primary
                        ? RedundancyType.Secondary
                        : RedundancyType.Primary;
                target.redundancyUnitId = this.unitId;
                target.redundancyPortIndex = port.port;
            }
        }
    }

    async duplicate(port: Port) {
        const options: SimpleDropdownOption[] = this.ports.map(port => {
            return { value: port.port, string: this.translate.instant('dlg.port', { num: port.port }) };
        });
        const dialogRef = this.dialog.open(DuplicateDestinationDialogComponent, { width: '472px', data: { options } });

        const destination = await lastValueFrom(dialogRef.afterClosed());
        if (destination) {
            const dest = this.ports.findIndex(pt => pt.port == destination);
            if (dest >= 0) {
                this.ports[dest] = { ...port, port: destination };
            }
        }
    }

    private findPort(unitId: string, portIndex: number): Port | undefined {
        for (const unit of this.commsList) {
            const owners = [unit.extComms, ...(unit.ceuUnits ?? [])];
            const owner = owners.find(o => o?.id === unitId);
            const port = owner?.Ports.find(p => p.port === portIndex);
            if (port) return port;
        }
        return undefined;
    }

    private clearRedundancy(port: Port): void {
        port.redundancy = false;
        port.redundancyType = null;
        port.redundancyUnitId = null;
        port.redundancyPortIndex = null;
    }
}
